import { chartSeriesBgClass, chartSeriesColor } from "~/components/charts";
import {
  formatBucketDate,
  formatCurrencyCompact,
  formatNumber,
} from "~/utils/formatting";
import type { TotalSalesViewModel } from "./map-sales-response";

export interface SummaryMarketEntry {
  market: string;
  color: string;
  colorClassName: string;
  formattedValue: string;
  sparkline: number[];
  isUnavailable?: boolean;
}

export interface SummaryCardsData {
  units: {
    perMarket: SummaryMarketEntry[];
  };
  sales: {
    perMarket: SummaryMarketEntry[];
    peak?: {
      date: string;
      perMarketFormatted: string;
    };
  };
  salesRevenueUnavailable: boolean;
}

const buildPeakFooter = (
  viewModel: TotalSalesViewModel,
): SummaryCardsData["sales"]["peak"] | undefined => {
  const { peak } = viewModel;
  if (!peak?.date || Object.keys(peak.byMarket).length === 0) {
    return undefined;
  }

  const perMarketFormatted = viewModel.totals.byMarket
    .map((market) => {
      const revenue = peak.byMarket[market.market]?.revenue;
      if (revenue == null) {
        return null;
      }
      return formatCurrencyCompact(revenue, market.currency);
    })
    .filter((value): value is string => value != null)
    .join(" · ");

  if (!perMarketFormatted) {
    return undefined;
  }

  return {
    date: formatBucketDate(peak.date),
    perMarketFormatted,
  };
};

export const buildSummaryCardsModel = (
  viewModel: TotalSalesViewModel,
  marketOrder: string[],
): SummaryCardsData => {
  if (viewModel.markets.length === 0 || viewModel.data.length === 0) {
    return {
      units: { perMarket: [] },
      sales: { perMarket: [] },
      salesRevenueUnavailable: false,
    };
  }

  const colorIndex = (market: string) => {
    const index = marketOrder.indexOf(market);
    return index >= 0 ? index : 0;
  };

  const perMarketUnits = viewModel.totals.byMarket.map((entry) => {
    const sparkline = viewModel.data.map((datum) => {
      const value = datum[entry.market];
      return typeof value === "number" ? value : 0;
    });
    const index = colorIndex(entry.market);

    return {
      market: entry.market,
      color: chartSeriesColor(index),
      colorClassName: chartSeriesBgClass(index),
      formattedValue: formatNumber(entry.units),
      sparkline,
    };
  });

  const perMarketSales = viewModel.totals.byMarket.map((entry) => {
    const { revenue } = entry;
    const revenueUnavailable = revenue == null;
    const sparkline = revenueUnavailable
      ? []
      : viewModel.data.map((datum) => datum.detail[entry.market]?.revenue ?? 0);

    return {
      market: entry.market,
      color: chartSeriesColor(colorIndex(entry.market)),
      colorClassName: chartSeriesBgClass(colorIndex(entry.market)),
      formattedValue: revenueUnavailable
        ? "—"
        : formatCurrencyCompact(revenue, entry.currency),
      sparkline,
      isUnavailable: revenueUnavailable,
    };
  });

  const salesRevenueUnavailable = perMarketSales.every(
    (entry) => entry.isUnavailable,
  );

  return {
    units: {
      perMarket: perMarketUnits,
    },
    sales: {
      perMarket: perMarketSales,
      peak: salesRevenueUnavailable ? undefined : buildPeakFooter(viewModel),
    },
    salesRevenueUnavailable,
  };
};
